/** Wraps XMLHttpRequest.open/send/setRequestHeader. */
import { getRuntime } from '../capture.js';
import { newSpan, traceHeaders, type SpanIds } from '../trace.js';
import { now } from '../util.js';

interface ArisXhrState {
  method: string;
  url: string;
  start: number;
  reqHeaders: Record<string, string>;
  bodySize: number | null;
  stack?: string;
  span: SpanIds;
}

const STATE = new WeakMap<XMLHttpRequest, ArisXhrState>();

let origOpen: XMLHttpRequest['open'] | null = null;
let origSend: XMLHttpRequest['send'] | null = null;
let origSetHeader: XMLHttpRequest['setRequestHeader'] | null = null;

export function installXhr(): () => void {
  if (typeof XMLHttpRequest === 'undefined' || origOpen) return () => {};

  origOpen = XMLHttpRequest.prototype.open;
  origSend = XMLHttpRequest.prototype.send;
  origSetHeader = XMLHttpRequest.prototype.setRequestHeader;

  const open = origOpen;
  const send = origSend;
  const setHeader = origSetHeader;

  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    const u = typeof url === 'string' ? url : url.href;
    STATE.set(this, {
      method, url: u, start: 0, reqHeaders: {}, bodySize: null, span: newSpan(),
    });
    // @ts-expect-error pass-through original signature
    return open.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (this: XMLHttpRequest, name: string, value: string) {
    const st = STATE.get(this);
    if (st) st.reqHeaders[name] = value;
    return setHeader.call(this, name, value);
  };

  XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const rt = getRuntime();
    const st = STATE.get(this);

    // Skip axios-originated requests if attachAxios() is in use (it captures them with richer context).
    const isAxios = 'x-aris-axios' in st?.reqHeaders! || 'X-Aris-Axios' in (st?.reqHeaders ?? {});

    if (rt && st && !isAxios && !rt.shouldIgnore(st.url)) {
      st.start = now();
      st.stack = new Error().stack;
      st.bodySize = typeof body === 'string' ? body.length : null;

      // Inline firewall: block before send.
      const policy = rt.enforce(st.url);
      if (policy.blocked) {
        const msg = `Blocked by Aris firewall (${policy.reason}): ${st.url}`;
        rt.capture({
          method: st.method, url: st.url, status: null, ok: false, durationMs: 0,
          requestHeaders: st.reqHeaders,
          error: { type: 'ArisFirewallBlock', message: msg },
          stack: st.stack, span: st.span, blocked: true,
        });
        throw new DOMException(msg, 'SecurityError');
      }

      // Inject trace headers.
      for (const [k, v] of Object.entries(traceHeaders(st.span, 'unknown'))) {
        try { setHeader.call(this, k, v); st.reqHeaders[k] = v; } catch { /* headers locked */ }
      }

      const finalize = (error: { type?: string; message?: string } | null) => {
        const respHeaders = parseRawHeaders(this.getAllResponseHeaders());
        rt.capture({
          method: st.method,
          url: st.url,
          status: error ? null : this.status || null,
          ok: !error && this.status >= 200 && this.status < 400,
          durationMs: now() - st.start,
          requestHeaders: st.reqHeaders,
          responseHeaders: respHeaders,
          requestBodySize: st.bodySize,
          responseBodySize: numHeader(respHeaders['content-length']),
          error,
          stack: st.stack,
          span: st.span,
        });
      };

      this.addEventListener('loadend', () => finalize(null), { once: true });
      this.addEventListener('error', () => finalize({ type: 'NetworkError', message: 'XHR error' }), { once: true });
      this.addEventListener('timeout', () => finalize({ type: 'TimeoutError', message: 'XHR timeout' }), { once: true });
    }
    return send.call(this, body ?? null);
  };

  return restoreXhr;
}

export function restoreXhr(): void {
  if (origOpen) XMLHttpRequest.prototype.open = origOpen;
  if (origSend) XMLHttpRequest.prototype.send = origSend;
  if (origSetHeader) XMLHttpRequest.prototype.setRequestHeader = origSetHeader;
  origOpen = origSend = origSetHeader = null;
}

function parseRawHeaders(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of (raw || '').trim().split(/[\r\n]+/)) {
    const idx = line.indexOf(':');
    if (idx > 0) out[line.slice(0, idx).trim().toLowerCase()] = line.slice(idx + 1).trim();
  }
  return out;
}

function numHeader(v: string | undefined): number | null {
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
